import { useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { runTakeoffJob } from "@/budget/lib/aiTakeoff";

const ACCEPT = ".pdf,.docx,.xlsx,.xlsm,.txt,.csv,.tsv,.md";
const MAX_MB = 15;

/**
 * Upload an ADDITIONAL takeoff and merge its parsed scopes/notes into the
 * current budget in the Builder. Project info is NEVER touched by a follow-up
 * upload — the user's existing project name / customer / etc. always win.
 *
 * Props:
 *  - onMerge(payload): called with the raw /api/ai-takeoff response so the
 *    parent (BudgetBuilder) can merge the new scopes + notes into state.
 *  - disabled: boolean (e.g. archived budgets are read-only).
 */
export default function AITakeoffAddButton({ onMerge, disabled }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    // Reset the input so picking the same filename twice still fires change.
    if (inputRef.current) inputRef.current.value = "";
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`File too large. Max ${MAX_MB} MB.`);
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const data = await runTakeoffJob(fd);
      const meta = data.ai_meta || {};
      toast.success(
        `Merged ${meta.scopes_extracted || 0} scope${meta.scopes_extracted === 1 ? "" : "s"} · ${meta.line_items_extracted || 0} line items from '${f.name}'`,
      );
      onMerge?.(data, f.name);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "AI takeoff failed";
      toast.error(String(msg).slice(0, 240));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => inputRef.current?.click()}
        disabled={disabled || busy}
        className="rounded-sm border-slate-300 text-slate-600 hover:bg-slate-50 gap-1.5 text-xs"
        data-testid="ai-takeoff-add-btn"
        title="Upload another takeoff and merge its scopes into this budget"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
        {busy ? "Parsing…" : "Add Files"}
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        onChange={onPick}
        className="sr-only"
        data-testid="ai-takeoff-add-input"
      />
    </>
  );
}
